import arePositionsEqual from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/arePositionsEqual';
import parseInstancePositions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/parseInstancePositions';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import type { ViewerFieldInfo } from '@verclam/docx-kit-editor';
import * as React from 'react';

export type FieldViewerReading = {
	/** The viewer's description of the field; `null` when the viewer does not know it yet. */
	readonly info: ViewerFieldInfo | null;
	/** How many times this very field (this list element, for a field in a list) is displayed. */
	readonly occurrenceCount: number;
};

/**
 * A reader of what the viewer shows for one Dixit field, fresh on every call: `handle.fields()` is a
 * live query, not a reactive value. Returns the unknown reading until the viewer answers.
 */
const useFieldViewerReader = (fieldKey: string): (() => FieldViewerReading) => {
	const { handleRef, fieldIndex } = useDocumentViewer();

	return React.useCallback(() => {
		const placement = fieldIndex.findPlacement(fieldKey);
		const fields = handleRef.current?.fields() ?? [];

		if (placement === null) {
			return { info: null, occurrenceCount: 0 };
		}

		const info = fields.find((candidate) => candidate.id === placement.uuid) ?? null;
		const occurrenceCount =
			info === null
				? 0
				: info.occurrences.filter((occurrence) =>
						arePositionsEqual(parseInstancePositions(occurrence.path), placement.positions),
					).length;

		return { info, occurrenceCount };
	}, [handleRef, fieldIndex, fieldKey]);
};

export default useFieldViewerReader;
